/* ═══════════════════════════════════════════════════════════════
   INSERTING A MODULE — the one flow, shared by both editors
   (docs/MASTERS_UNIFIED_PLAN.md Phase 5).

   ⛔ A MODULE is a reusable block-group, single-spec only — never a
   variant. ⛔ An insert is a stamped COPY, never a live link (v2.12
   grave): nothing here ever reads the library again to refresh what
   landed.

   What this owns:
     · the library list (live modules only) and its refresh
     · the INSERT itself — anchor-match every chunk, land the ones that
       fit, hand the rest to the placement tray
     · place-mode: an armed chip lands where you click, with a drop line
       showing the boundary before you commit
     · the two destructive confirmations (discard a chunk, delete an
       insertion) and the leave guard

   The hosts (Spec Studio, Designer) own only their own wiring: the
   panel's dock entry, the block-menu items, and rendering `overlay`.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import type { EditorState } from '@tiptap/pm/state';
import type { ContentBlock, StandardConfig } from '../../shared/types';
import { computeNumbering } from '../../shared/body-layout';
import type {
  AnchorMatch,
  ModuleChunk,
  ModuleEditState,
  ModuleStamp,
  ModuleUpdateState,
  SpecModule,
} from '../../shared/spec-modules';
import {
  anchorPathLabel,
  listModuleInsertions,
  moduleStampLabel,
  moduleUpdateState,
  newInsertId,
  remapInsertGroups,
  stampChunkForInsert,
} from '../../shared/spec-modules';
import { createGroupRemap } from '../../shared/block-groups';
import { findHeadingMergeTarget, type HeadingMergeTarget } from '../../shared/spec-assemble';
import type { VariantChange, VariantChangeEntry } from '../../shared/variant-updates';
import { buildLocalModuleUpdates, localDeviationPatches } from '../../shared/module-updates';
import { docToBlocks, newBlockId, newGroupId } from '../designer/schema';
import {
  applyLocalModuleChange,
  boundaryIndexAtCoords,
  boundaryRect,
  insertModuleDrops,
  insertionBlockIds,
  insertionRunIds,
  moduleStampKey,
  removeInsertion,
  restampInsertionNodes,
  selectInsertionRun,
  stampOfBlock,
  topBlocks,
  type InsertionTagInfo,
  type ModuleDrop,
} from '../designer/moduleInsert';
import { planModuleInsert } from '../lib/moduleInsertPlacement';
import { ModuleService } from '../services/moduleService';
import { useTableSync } from '../hooks/useTableSync';
import { ConfirmDialog } from './ui/confirm-dialog';

/** A chunk that could not place itself — a "place me" chip in the tray. Its
 *  blocks are ALREADY stamped, so placing one is a plain insert with no second
 *  decision to make. Session state only: chips die with the editor. */
export interface PendingChunk {
  key: string;
  moduleId: string;
  moduleName: string;
  moduleVersion: number;
  insertId: string;
  /** The anchor as a human reads it — "PART 2 - PRODUCTS › 2.03 Materials". */
  label: string;
  blockCount: number;
  reason: 'ambiguous' | 'none';
  blocks: ContentBlock[];
}

/** One module insertion as the "In this document" list reads it (R2 Phase 2). */
export interface DocModule {
  insertId: string;
  moduleId: string;
  moduleName: string;
  /** The version this copy has answered. */
  moduleVersion: number;
  /** The library's version now, when the module is still in the library. */
  libraryVersion?: number;
  blockCount: number;
  /** Edited in this spec since it landed? `unknown` on pre-R2 inserts. */
  state: ModuleEditState;
  /** Is the library ahead, and has this copy said anything about it? */
  update: ModuleUpdateState;
  /** The derived update entry, when there is something to review. */
  entryId?: string;
  /** Offers still waiting for an answer in that entry. */
  pendingUpdates: number;
  /** 1-based, and how many drops of the SAME module this document holds —
   *  set only when there is more than one, so a single insert reads clean. */
  occurrence?: number;
  occurrences?: number;
}

/** The app toast's options (app.tsx `toast`). */
export interface ToastOptions {
  action?: { label: string; onClick: () => void };
  ms?: number;
}

export interface UseModuleInsertOptions {
  editor: Editor | null;
  /** The TARGET's edit gate. Inserting rides it — there is no insert-only
   *  permission (docs/MASTERS_UNIFIED_PLAN.md Phase 5). */
  canEdit: boolean;
  /** `modules.view`. False ⇒ the library is never fetched and the panel is
   *  never offered. */
  canView: boolean;
  /** The forma the target numbers with, when it has one (project specs do;
   *  masters render on the CSI default). Anchors match on heading TEXT, so
   *  this only sharpens the prefix tie-breaker. */
  forma?: StandardConfig | null;
  /** The app toast. The optional third argument (an action button) is what
   *  the app's own `toast` already accepts — a drop uses it for "Show". */
  toast: (msg: string, isError?: boolean, opts?: ToastOptions) => void;
  /** The Spec Studio's gutter selection layer, when the host has one — it is
   *  what makes "Select insertion" name every block of a non-contiguous
   *  insertion at once. The Designer has no gutter and passes nothing. */
  selectBlocks?: (ids: string[]) => void;
  /** R2 Phase 3: derive "the library moved" updates for this document and
   *  offer them per paragraph. The Spec Masters Studio turns this on (it has
   *  the Updates panel to answer them in). The Designer does NOT: ⛔ nothing
   *  ever PRs into a project spec — those copies are hand-managed. */
  updatesEnabled?: boolean;
  /** The master this document belongs to, for the derived entry's header. */
  specMasterId?: string;
  /** WHERE a library update gets answered.
   *    · 'panel'  the Spec Masters Studio — the Updates panel, per paragraph
   *    · 'dialog' the Designer — no Updates panel exists there, so the offer
   *               is a whole-module take/keep in a dialog of its own. A
   *               project spec is still never SILENT about a module moving
   *               (Ruling 3), it just answers as a whole. */
  reviewMode?: 'panel' | 'dialog';
  /** Insert… places a single-chunk module right after the block the editor
   *  caret is in, when that block is inside a Part (plan F3a; fed from the
   *  editor selection at insert time). Off ⇒ every chunk uses its anchor
   *  match, as before. Default on. A caret outside any Part, and every
   *  multi-chunk module, still anchor-match. */
  insertAtCaret?: boolean;
}

export interface ModuleInsertApi {
  modules: SpecModule[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  busyModuleId: string | null;
  insertModule: (module: SpecModule) => void;

  chips: PendingChunk[];
  armedKey: string | null;
  arm: (key: string) => void;
  discard: (key: string) => void;

  /** Right-click actions on a stamped block. */
  stampAt: (blockId: string) => ModuleStamp | null;
  selectInsertion: (insertId: string, blockId: string) => void;
  requestRemoveInsertion: (stamp: ModuleStamp) => void;

  /* ── "In this document" + library updates (R2 Phases 2–3) ── */
  /** Every module standing in the open document, in document order. */
  inDocument: DocModule[];
  /** Scroll to and select every block of one insertion. */
  revealInsertion: (insertId: string) => void;
  /** Remove one insertion, confirmed — stronger wording when it was edited. */
  requestRemoveByInsertId: (insertId: string) => void;
  /** The derived update entries the Updates panel shows alongside the raised
   *  feed. Empty unless `updatesEnabled`. */
  localUpdates: VariantChangeEntry[];
  /** True for entries this hook owns — the host routes decisions by it. */
  ownsEntry: (entryId: string) => boolean;
  decideLocal: (entryId: string, changeId: string, decision: 'accepted' | 'rejected') => void;
  decideLocalAll: (entryId: string, decision: 'accepted' | 'rejected') => void;
  /** The panel scrolls to this card when an "update available" badge is
   *  clicked; null the rest of the time. */
  focusLocalChangeId: string | null;
  clearLocalFocus: () => void;
  /** Open (or re-open, after "Keep all mine") one insertion's update. Answers
   *  the entry id so the host can show the panel. */
  reviewInsertion: (insertId: string) => string | null;
  /** DIALOG MODE (the Designer): the offer currently open in the Modules
   *  panel, answered whole. Null in panel mode, and null when nothing is open. */
  reviewFor: {
    insertId: string;
    entryId: string;
    moduleName: string;
    fromVersion: number;
    toVersion: number;
    count: number;
  } | null;
  decideReview: (decision: 'accepted' | 'rejected') => void;
  closeReview: () => void;

  /** Call FIRST in the host's leave handler: true ⇒ a confirmation is now on
   *  screen and the host must not navigate. */
  guardLeave: (proceed: () => void) => boolean;
  /** A name tag in the document was clicked: the panel shows this insertion's
   *  row. `seq` changes on every click so a second click re-reveals it. */
  panelReveal: { insertId: string; seq: number } | null;

  /** The drop line and every dialog this flow raises. Render it once. */
  overlay: React.ReactNode;
}

/** The top-level block the caret (selection start) is inside, if any. */
function caretBlockIdOf(state: EditorState): string | null {
  const from = state.selection.from;
  for (const t of topBlocks(state)) {
    if (t.id && from >= t.pos && from < t.pos + t.node.nodeSize) return t.id;
  }
  return null;
}

let chipSeq = 0;

/** Every chunk of one module, stamped for ONE insertion — fresh block ids,
 *  and every captured group re-minted through ONE remap for the whole
 *  insert (shared/block-groups), so two inserts of a module are two
 *  independent groups. Shared by Insert and the drag-drop. */
function stampModule(
  module: SpecModule,
  stamp: ModuleStamp,
  mergeFor?: (chunk: ModuleChunk) => HeadingMergeTarget | null
): Array<{ chunk: ModuleChunk; stamped: ContentBlock[]; merge: HeadingMergeTarget | null }> {
  const remap = createGroupRemap(newGroupId);
  const out: Array<{
    chunk: ModuleChunk;
    stamped: ContentBlock[];
    merge: HeadingMergeTarget | null;
  }> = [];
  for (const chunk of module.chunks || []) {
    const merge = mergeFor ? mergeFor(chunk) : null;
    let stamped = remapInsertGroups(
      stampChunkForInsert(chunk, merge ? { ...stamp, mergedHeading: true } : stamp),
      remap
    );
    // Merged: the spec's own heading stays; the chunk's duplicate is omitted.
    if (merge) stamped = stamped.slice(1);
    if (stamped.length) out.push({ chunk, stamped, merge });
  }
  return out;
}

/** A chunk that could not place itself → its "place me" chip. */
function chipFor(
  module: SpecModule,
  stamp: ModuleStamp,
  chunk: ModuleChunk,
  stamped: ContentBlock[],
  match: AnchorMatch
): PendingChunk {
  return {
    key: `chip-${++chipSeq}`,
    moduleId: module.id,
    moduleName: module.name,
    moduleVersion: stamp.moduleVersion,
    insertId: stamp.insertId,
    label: anchorPathLabel(chunk.anchor),
    blockCount: stamped.length,
    reason: match.kind === 'ambiguous' ? 'ambiguous' : 'none',
    blocks: stamped,
  };
}

export function useModuleInsert({
  editor,
  canEdit,
  canView,
  forma,
  toast,
  selectBlocks,
  updatesEnabled = false,
  specMasterId,
  reviewMode = 'panel',
  insertAtCaret = true,
}: UseModuleInsertOptions): ModuleInsertApi {
  const [modules, setModules] = React.useState<SpecModule[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busyModuleId, setBusyModuleId] = React.useState<string | null>(null);
  const [chips, setChips] = React.useState<PendingChunk[]>([]);
  const [armedKey, setArmedKey] = React.useState<string | null>(null);
  const [dropLine, setDropLine] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [discardKey, setDiscardKey] = React.useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<{
    insertId: string;
    label: string;
    modified: boolean;
  } | null>(null);
  const [leaveProceed, setLeaveProceed] = React.useState<{ run: () => void } | null>(null);
  const [acceptAllTarget, setAcceptAllTarget] = React.useState<{
    entryId: string;
    moduleName: string;
  } | null>(null);

  const toastRef = React.useRef(toast);
  toastRef.current = toast;

  /* ─── The library ─── */

  const load = React.useCallback(() => {
    if (!canView) {
      setModules([]);
      return;
    }
    setLoading(true);
    ModuleService.list()
      .then((rows) => {
        setModules(rows);
        setError(null);
      })
      .catch((err: unknown) =>
        setError(
          err instanceof Error && err.message ? err.message : 'The module library could not load'
        )
      )
      .finally(() => setLoading(false));
  }, [canView]);

  React.useEffect(() => load(), [load]);
  // Somebody else saved, renamed or archived a module: the list follows.
  useTableSync(canView ? ['module_library'] : null, load);

  /* ─── The insert ─── */

  const formaRef = React.useRef<StandardConfig | null>(forma ?? null);
  formaRef.current = forma ?? null;

  const insertModule = React.useCallback(
    (module: SpecModule) => {
      if (!editor || editor.isDestroyed) return;
      if (!canEdit) {
        toastRef.current('This spec is read-only for you', true);
        return;
      }
      const chunks = module.chunks || [];
      if (!chunks.length) {
        toastRef.current(`“${module.name}” has nothing in it`, true);
        return;
      }

      setBusyModuleId(module.id);
      try {
        const state = editor.state;
        // The document as it stands on screen — the same shape a save writes,
        // so the outline the match walks is the outline the user is looking at.
        const blocks = docToBlocks(state.doc.toJSON());
        const refs = computeNumbering(blocks, formaRef.current);

        const stamp: ModuleStamp = {
          moduleId: module.id,
          moduleName: module.name,
          moduleVersion: module.version ?? 1,
          insertId: newInsertId(),
        };

        // A heading that already exists in the chunk's Part wins: never two of
        // the same article. Then every article lands in ITS part — the caret
        // only places articles with no part (planModuleInsert, TWEAKS item 13).
        const stampedChunks = stampModule(module, stamp, (c) =>
          findHeadingMergeTarget(blocks, c, c.anchor?.partIndex ?? 0, refs)
        );
        const merged = stampedChunks.flatMap((c) => (c.merge ? [c.merge.label] : []));
        const plan = planModuleInsert({
          items: stampedChunks,
          blocks,
          refs,
          caretBlockId: insertAtCaret ? caretBlockIdOf(state) : null,
          mintId: newBlockId,
        });
        const drops: ModuleDrop[] = plan.drops;
        const waiting: PendingChunk[] = plan.waiting.map(({ item, match }) =>
          chipFor(module, stamp, item.chunk, item.stamped, match)
        );

        if (drops.length) {
          insertModuleDrops(state, editor.view.dispatch.bind(editor.view), drops);
        }
        if (waiting.length) setChips((c) => [...c, ...waiting]);

        // Created part headings are the spec's structure, not module content.
        const landed =
          drops.reduce((n, d) => n + d.blocks.length, 0) - plan.createdHeadingIds.length;
        if (landed && waiting.length) {
          toastRef.current(
            `${landed} block${landed === 1 ? '' : 's'} placed — ${waiting.length} chunk${
              waiting.length === 1 ? ' needs' : 's need'
            } a spot`
          );
        } else if (landed) {
          toastRef.current(
            `Inserted “${module.name}” — ${landed} block${landed === 1 ? '' : 's'}` +
              (merged.length ? ` (merges into ${merged.join(', ')})` : '')
          );
        } else if (merged.length) {
          toastRef.current(
            `“${module.name}” merges into ${merged.join(', ')} — nothing new to add`
          );
        } else {
          toastRef.current(
            `Nothing in this spec matches where “${module.name}” was captured — place it by hand`
          );
        }
      } catch (err) {
        toastRef.current(
          err instanceof Error && err.message ? err.message : 'That module could not be inserted',
          true
        );
      } finally {
        setBusyModuleId(null);
      }
    },
    [editor, canEdit, insertAtCaret]
  );

  /* ─── Place-mode: an armed chip lands where you click ─── */

  const armedRef = React.useRef<string | null>(null);
  armedRef.current = armedKey;
  const chipsRef = React.useRef<PendingChunk[]>(chips);
  chipsRef.current = chips;

  const arm = React.useCallback(
    (key: string) => setArmedKey((current) => (current === key ? null : key)),
    []
  );

  const disarm = React.useCallback(() => {
    setArmedKey(null);
    setDropLine(null);
  }, []);

  React.useEffect(() => {
    if (!editor || editor.isDestroyed || !armedKey) return;
    const view = editor.view;
    const dom = view.dom as HTMLElement;
    dom.classList.add('sf-mod-placing');
    // The panel may be living as a toolbar DROPDOWN, which closes on any
    // mousedown outside itself. While a chunk is armed the next such click is
    // the placement, so the tray has to stay put (PanelDropdown reads this).
    document.body.classList.add('sf-mod-armed');

    const onMove = (e: MouseEvent) => {
      setDropLine(boundaryRect(view, boundaryIndexAtCoords(view, e.clientY)));
    };
    const onDown = (e: MouseEvent) => {
      const chip = chipsRef.current.find((c) => c.key === armedRef.current);
      if (!chip) return;
      // The click PLACES — it must not also move the caret or start a
      // selection drag underneath the insert.
      e.preventDefault();
      e.stopPropagation();
      const index = boundaryIndexAtCoords(view, e.clientY);
      insertModuleDrops(view.state, view.dispatch.bind(view), [
        // Groups are solid: a click between two grouped blocks lands after them.
        { blocks: chip.blocks, atIndex: index, outsideGroups: true },
      ]);
      setChips((c) => c.filter((x) => x.key !== chip.key));
      setArmedKey(null);
      setDropLine(null);
      toastRef.current(`Placed ${chip.blockCount} block${chip.blockCount === 1 ? '' : 's'}`);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      disarm();
    };

    dom.addEventListener('mousemove', onMove);
    dom.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      dom.classList.remove('sf-mod-placing');
      document.body.classList.remove('sf-mod-armed');
      dom.removeEventListener('mousemove', onMove);
      dom.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [editor, armedKey, disarm]);

  // A chip that was armed and then discarded (or placed) leaves place-mode.
  React.useEffect(() => {
    if (armedKey && !chips.some((c) => c.key === armedKey)) disarm();
  }, [chips, armedKey, disarm]);

  const [panelReveal, setPanelReveal] = React.useState<{ insertId: string; seq: number } | null>(
    null
  );

  /* ─── The name tag on each piece (modules-polish, item 4) ───
     Clicking a tag selects that piece's run and shows its row in the panel.
     The tag is a widget decoration (moduleInsert.ts) — chrome, never content.
     A press on it never moves the caret; in the Spec Studio a press that
     TRAVELS is the gutter's piece drag (BlockGutter), not a click. */
  React.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const dom = editor.view.dom as HTMLElement;
    const tagOf = (e: Event): HTMLElement | null => {
      const t = e.target as HTMLElement | null;
      return t && typeof t.closest === 'function'
        ? t.closest<HTMLElement>('[data-sf-mod-tag]')
        : null;
    };
    const onDown = (e: MouseEvent) => {
      if (tagOf(e)) e.preventDefault();
    };
    const onClick = (e: MouseEvent) => {
      const tag = tagOf(e);
      if (!tag) return;
      e.preventDefault();
      const insertId = tag.getAttribute('data-sf-mod-tag') || '';
      const runStart = tag.getAttribute('data-sf-mod-run') || '';
      if (!insertId) return;
      if (selectBlocks) {
        const ids = insertionRunIds(editor.state, insertId, runStart);
        if (ids.length) selectBlocks(ids);
      } else {
        selectInsertionRun(
          editor.state,
          editor.view.dispatch.bind(editor.view),
          insertId,
          runStart
        );
      }
      setPanelReveal((prev) => ({ insertId, seq: (prev?.seq ?? 0) + 1 }));
    };
    dom.addEventListener('mousedown', onDown);
    dom.addEventListener('click', onClick);
    return () => {
      dom.removeEventListener('mousedown', onDown);
      dom.removeEventListener('click', onClick);
    };
  }, [editor, selectBlocks]);

  /* ─── Destructive paths, both confirmed ─── */

  const discard = React.useCallback((key: string) => setDiscardKey(key), []);

  const doDiscard = React.useCallback(() => {
    setChips((c) => c.filter((x) => x.key !== discardKey));
    setDiscardKey(null);
  }, [discardKey]);

  const stampAt = React.useCallback(
    (blockId: string): ModuleStamp | null =>
      editor && !editor.isDestroyed ? stampOfBlock(editor.state, blockId) : null,
    [editor]
  );

  const selectInsertion = React.useCallback(
    (insertId: string, blockId: string) => {
      if (!editor || editor.isDestroyed) return;
      const ids = insertionBlockIds(editor.state, insertId);
      if (!ids.length) return;
      if (selectBlocks) {
        // The gutter selection names every block, contiguous or not — which is
        // what "as a unit" means where that layer exists.
        selectBlocks(ids);
        return;
      }
      // No gutter here: select the run the click landed in and SAY when the
      // insertion reaches further, rather than swallowing the untouched blocks
      // between two chunks in one range.
      const picked = selectInsertionRun(
        editor.state,
        editor.view.dispatch.bind(editor.view),
        insertId,
        blockId
      );
      if (picked && picked.selected < picked.total) {
        toastRef.current(
          `This insertion sits in more than one place — selected the ${picked.selected} of ${picked.total} blocks you clicked`
        );
      }
    },
    [editor, selectBlocks]
  );

  /* ─── "In this document" (R2 Phase 2) ─────────────────────────
     Everything below is DERIVED from the document on screen: what is in it,
     whether it still matches what landed, and whether the library has moved
     since. Nothing is stored, so nothing can fall out of sync — the price is
     a re-read of the blocks, which is debounced because typing must never
     wait for a panel. */

  const [docTick, setDocTick] = React.useState(0);
  React.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    let timer: number | undefined;
    const bump = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setDocTick((v) => v + 1), 250);
    };
    // `transaction` (not `update`) so a document LOADED into the editor
    // counts too — the panel has to be right the moment a spec opens.
    const onTransaction = ({ transaction }: { transaction: { docChanged: boolean } }): void => {
      if (transaction.docChanged) bump();
    };
    editor.on('transaction', onTransaction);
    bump();
    return () => {
      window.clearTimeout(timer);
      editor.off('transaction', onTransaction);
    };
  }, [editor]);

  const docBlocks = React.useMemo(() => {
    if (!editor || editor.isDestroyed) return [] as ContentBlock[];
    try {
      return docToBlocks(editor.state.doc.toJSON());
    } catch {
      return [] as ContentBlock[];
    }
    // docTick is the whole point: the document moved, read it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, docTick]);

  const insertions = React.useMemo(() => listModuleInsertions(docBlocks), [docBlocks]);

  const libraryVersions = React.useMemo(
    () => new Map(modules.map((m) => [m.id, m.version ?? 1])),
    [modules]
  );

  /** Answers given in this session, per derived entry. Accepted offers vanish
   *  from the next derive (the document now matches), so whether anything was
   *  TAKEN is remembered here — it decides how the insertion closes out. */
  const [answers, setAnswers] = React.useState<
    Record<string, { decisions: Record<string, 'accepted' | 'rejected'>; took: boolean }>
  >({});
  /** Insertions whose "Keep all mine" the user has asked to see again. */
  const [reopened, setReopened] = React.useState<Set<string>>(new Set());
  /** Dialog mode: the entry on screen, and an insertion whose dismissed offer
   *  was just re-opened and is waiting for the next derive to rebuild it. */
  const [reviewEntryId, setReviewEntryId] = React.useState<string | null>(null);
  const [pendingReview, setPendingReview] = React.useState<string | null>(null);
  const [focusLocalChangeId, setFocusLocalChangeId] = React.useState<string | null>(null);

  const localUpdates = React.useMemo<VariantChangeEntry[]>(() => {
    if (!updatesEnabled) return [];
    const wanted = insertions
      .filter((i) => {
        const state = moduleUpdateState(i, libraryVersions.get(i.moduleId));
        return state === 'available' || (state === 'dismissed' && reopened.has(i.insertId));
      })
      .map((i) => i.insertId);
    if (!wanted.length) return [];
    const built = buildLocalModuleUpdates({
      modules,
      blocks: docBlocks,
      insertIds: wanted,
      ...(specMasterId ? { specMasterId } : {}),
    });
    return built.map((entry) => {
      const answered = answers[entry.id];
      if (!answered) return entry;
      const changes = entry.changes.map((c) =>
        answered.decisions[c.changeId] ? { ...c, decision: answered.decisions[c.changeId] } : c
      );
      return {
        ...entry,
        changes,
        status: changes.every((c) => c.decision !== 'pending')
          ? ('resolved' as const)
          : ('pending' as const),
      };
    });
  }, [
    updatesEnabled,
    insertions,
    libraryVersions,
    reopened,
    modules,
    docBlocks,
    specMasterId,
    answers,
  ]);

  const inDocument = React.useMemo<DocModule[]>(() => {
    const seen = new Map<string, number>();
    const total = new Map<string, number>();
    for (const i of insertions) total.set(i.moduleId, (total.get(i.moduleId) || 0) + 1);

    return insertions.map((i) => {
      const nth = (seen.get(i.moduleId) || 0) + 1;
      seen.set(i.moduleId, nth);
      const libraryVersion = libraryVersions.get(i.moduleId);
      const entry = localUpdates.find((e) => e.insertId === i.insertId);
      const state = moduleUpdateState(i, libraryVersion);
      const many = (total.get(i.moduleId) || 1) > 1;
      return {
        insertId: i.insertId,
        moduleId: i.moduleId,
        moduleName: i.moduleName,
        moduleVersion: i.moduleVersion,
        ...(libraryVersion != null ? { libraryVersion } : {}),
        blockCount: i.blockCount,
        state: i.state,
        // An entry that built nothing means the words are identical anyway —
        // a version number alone is not news worth a badge.
        update: entry ? 'available' : state === 'dismissed' ? 'dismissed' : 'none',
        ...(entry ? { entryId: entry.id } : {}),
        pendingUpdates: entry ? entry.changes.filter((c) => c.decision === 'pending').length : 0,
        ...(many ? { occurrence: nth, occurrences: total.get(i.moduleId) as number } : {}),
      };
    });
  }, [insertions, libraryVersions, localUpdates]);

  // The tags in the document read the SAME derived state the panel rows do —
  // handed to the paint as plugin meta (no document change, no undo step).
  const tagInfoKeyRef = React.useRef('');
  React.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const info: Record<string, InsertionTagInfo> = {};
    for (const d of inDocument) {
      info[d.insertId] = { moduleVersion: d.moduleVersion, state: d.state, update: d.update };
    }
    const key = JSON.stringify(info);
    if (key === tagInfoKeyRef.current) return;
    tagInfoKeyRef.current = key;
    editor.view.dispatch(
      editor.state.tr.setMeta(moduleStampKey, { info }).setMeta('addToHistory', false)
    );
  }, [editor, inDocument]);

  const revealInsertion = React.useCallback(
    (insertId: string) => {
      if (!editor || editor.isDestroyed) return;
      const ids = insertionBlockIds(editor.state, insertId);
      if (!ids.length) return;
      selectInsertion(insertId, ids[0]);
    },
    [editor, selectInsertion]
  );

  const removeLabelFor = React.useCallback(
    (insertId: string): { label: string; modified: boolean } => {
      const found = insertions.find((i) => i.insertId === insertId);
      return {
        label: found ? `${found.moduleName} v${found.moduleVersion}` : 'this module',
        modified: found?.state === 'modified',
      };
    },
    [insertions]
  );

  const requestRemoveByInsertId = React.useCallback(
    (insertId: string) => setRemoveTarget({ insertId, ...removeLabelFor(insertId) }),
    [removeLabelFor]
  );

  const requestRemoveInsertion = React.useCallback(
    (stamp: ModuleStamp) => {
      const derived = removeLabelFor(stamp.insertId);
      setRemoveTarget({
        insertId: stamp.insertId,
        label: moduleStampLabel(stamp),
        modified: derived.modified,
      });
    },
    [removeLabelFor]
  );

  const doRemove = React.useCallback(() => {
    const target = removeTarget;
    setRemoveTarget(null);
    if (!target || !editor || editor.isDestroyed) return;
    const count = insertionBlockIds(editor.state, target.insertId).length;
    removeInsertion(editor.state, editor.view.dispatch.bind(editor.view), target.insertId);
    toastRef.current(
      `Deleted ${count} block${count === 1 ? '' : 's'} from “${target.label}” — Ctrl+Z brings them back`
    );
  }, [removeTarget, editor]);

  /* ─── Answering a library update (R2 Phase 3) ─── */

  const ownsEntry = React.useCallback(
    (entryId: string) => localUpdates.some((e) => e.id === entryId),
    [localUpdates]
  );

  /** Close the insertion out once every offer has an answer: took something ⇒
   *  this copy is AT the new version; took nothing ⇒ record the decline so the
   *  badge goes quiet until a NEWER version arrives. */
  const settle = React.useCallback(
    (entry: VariantChangeEntry, took: boolean, answered: VariantChange[]) => {
      if (!editor || editor.isDestroyed || !entry.insertId) return;
      const toVersion = entry.moduleToVersion ?? entry.toVersion ?? 1;
      restampInsertionNodes(
        editor.state,
        editor.view.dispatch.bind(editor.view),
        entry.insertId,
        took ? { moduleVersion: toVersion } : { dismissedVersion: toVersion },
        // Every paragraph that was knowingly declined records that it now
        // differs from the module — so the panel reads "● modified here"
        // instead of pretending this copy still matches.
        took ? localDeviationPatches(answered) : {}
      );
      setAnswers((prev) => {
        if (!prev[entry.id]) return prev;
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
      setReopened((prev) => {
        if (!prev.has(entry.insertId as string)) return prev;
        const next = new Set(prev);
        next.delete(entry.insertId as string);
        return next;
      });
    },
    [editor]
  );

  const decideLocal = React.useCallback(
    (entryId: string, changeId: string, decision: 'accepted' | 'rejected') => {
      const entry = localUpdates.find((e) => e.id === entryId);
      const change = entry?.changes.find((c) => c.changeId === changeId);
      if (!entry || !change || change.decision !== 'pending') return;
      if (!editor || editor.isDestroyed) return;
      if (!canEdit) {
        toastRef.current('This spec is read-only for you', true);
        return;
      }

      if (decision === 'accepted') {
        applyLocalModuleChange(
          editor.state,
          editor.view.dispatch.bind(editor.view),
          change,
          'accepted'
        );
      }
      const took = (answers[entryId]?.took ?? false) || decision === 'accepted';
      const stillOpen = entry.changes.some(
        (c) => c.changeId !== changeId && c.decision === 'pending'
      );
      if (stillOpen) {
        setAnswers((prev) => ({
          ...prev,
          [entryId]: {
            decisions: { ...(prev[entryId]?.decisions || {}), [changeId]: decision },
            took,
          },
        }));
        return;
      }
      settle(
        entry,
        took,
        entry.changes.map((c) => (c.changeId === changeId ? { ...c, decision } : c))
      );
      toastRef.current(
        took
          ? `“${entry.moduleName}” is now at v${entry.moduleToVersion ?? entry.toVersion}`
          : `Kept this copy of “${entry.moduleName}” — the next version will ask again`
      );
    },
    [localUpdates, editor, canEdit, answers, settle]
  );

  const applyAll = React.useCallback(
    (entryId: string, decision: 'accepted' | 'rejected') => {
      const entry = localUpdates.find((e) => e.id === entryId);
      if (!entry || !editor || editor.isDestroyed) return;
      if (!canEdit) {
        toastRef.current('This spec is read-only for you', true);
        return;
      }
      const pending = entry.changes.filter((c) => c.decision === 'pending');
      if (!pending.length) return;

      if (decision === 'accepted') {
        // Each answer is its own transaction against the document as it now
        // stands — the same path one card at a time takes.
        for (const change of pending) {
          applyLocalModuleChange(
            editor.state,
            editor.view.dispatch.bind(editor.view),
            change,
            'accepted'
          );
        }
      }
      const took = (answers[entryId]?.took ?? false) || decision === 'accepted';
      settle(
        entry,
        took,
        entry.changes.map((c) => (c.decision === 'pending' ? { ...c, decision } : c))
      );
      toastRef.current(
        took
          ? `Took v${entry.moduleToVersion ?? entry.toVersion} of “${entry.moduleName}”`
          : `Kept this copy of “${entry.moduleName}” — the next version will ask again`
      );
    },
    [localUpdates, editor, canEdit, answers, settle]
  );

  const decideLocalAll = React.useCallback(
    (entryId: string, decision: 'accepted' | 'rejected') => {
      const entry = localUpdates.find((e) => e.id === entryId);
      if (!entry) return;
      const insertion = insertions.find((i) => i.insertId === entry.insertId);
      // Taking the whole update over words somebody edited here is the one
      // move that loses work — it gets asked about.
      if (decision === 'accepted' && insertion?.state === 'modified') {
        setAcceptAllTarget({ entryId, moduleName: entry.moduleName || 'this module' });
        return;
      }
      applyAll(entryId, decision);
    },
    [localUpdates, insertions, applyAll]
  );

  const reviewInsertion = React.useCallback(
    (insertId: string): string | null => {
      const found = inDocument.find((d) => d.insertId === insertId);
      if (found?.entryId) {
        const entry = localUpdates.find((e) => e.id === found.entryId);
        if (reviewMode === 'dialog') setReviewEntryId(found.entryId);
        else setFocusLocalChangeId(entry?.changes[0]?.changeId ?? null);
        return found.entryId;
      }
      if (found?.update === 'dismissed') {
        // Re-opening a dismissed update: the next derive builds it again, and
        // `pendingReview` opens it the moment it exists.
        setReopened((prev) => new Set(prev).add(insertId));
        setFocusLocalChangeId(null);
        setPendingReview(insertId);
        return null;
      }
      return null;
    },
    [inDocument, localUpdates, reviewMode]
  );

  // A re-opened offer, once the derive has rebuilt it.
  React.useEffect(() => {
    if (!pendingReview) return;
    const entry = localUpdates.find((e) => e.insertId === pendingReview);
    if (!entry) return;
    if (reviewMode === 'dialog') setReviewEntryId(entry.id);
    else setFocusLocalChangeId(entry.changes[0]?.changeId ?? null);
    setPendingReview(null);
  }, [pendingReview, localUpdates, reviewMode]);

  const reviewEntry = React.useMemo(
    () => localUpdates.find((e) => e.id === reviewEntryId) || null,
    [localUpdates, reviewEntryId]
  );

  /* The whole-module offer, as the Modules panel draws it INLINE. No modal:
     the panel often lives in a dropdown, and a dialog inside one is a layering
     fight nobody wins. Per-paragraph cards stay in the Studio, which has the
     Updates panel to hold them. */
  const reviewFor = React.useMemo(
    () =>
      reviewEntry && reviewEntry.insertId
        ? {
            insertId: reviewEntry.insertId,
            entryId: reviewEntry.id,
            moduleName: reviewEntry.moduleName || 'This module',
            fromVersion: reviewEntry.moduleFromVersion ?? reviewEntry.fromVersion ?? 1,
            toVersion: reviewEntry.moduleToVersion ?? reviewEntry.toVersion ?? 1,
            count: reviewEntry.changes.length,
          }
        : null,
    [reviewEntry]
  );

  const closeReview = React.useCallback(() => setReviewEntryId(null), []);

  const decideReview = React.useCallback(
    (decision: 'accepted' | 'rejected') => {
      const entry = reviewEntry;
      setReviewEntryId(null);
      if (entry) decideLocalAll(entry.id, decision);
    },
    [reviewEntry, decideLocalAll]
  );

  /* ─── Leaving with chunks still in the tray ─── */

  const guardLeave = React.useCallback((proceed: () => void): boolean => {
    if (!chipsRef.current.length) return false;
    setLeaveProceed({ run: proceed });
    return true;
  }, []);

  const removeCount =
    removeTarget && editor && !editor.isDestroyed
      ? insertionBlockIds(editor.state, removeTarget.insertId).length
      : 0;
  const discarding = chips.find((c) => c.key === discardKey) || null;

  const overlay = (
    <>
      {dropLine && (
        <div
          className="sf-mod-dropline"
          aria-hidden="true"
          style={{ top: dropLine.top, left: dropLine.left, width: dropLine.width }}
        />
      )}

      <ConfirmDialog
        open={!!discarding}
        onOpenChange={(o) => !o && setDiscardKey(null)}
        title="Discard this chunk?"
        description={
          discarding
            ? `${discarding.blockCount} block${
                discarding.blockCount === 1 ? '' : 's'
              } from “${discarding.moduleName}” have not been placed. Discarding drops them — the module itself is untouched.`
            : ''
        }
        confirmLabel="Discard"
        onConfirm={doDiscard}
      />

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title={
          removeTarget?.modified
            ? 'This module was edited in this spec — remove anyway?'
            : 'Delete this insertion?'
        }
        description={
          removeTarget
            ? `${removeCount} block${removeCount === 1 ? '' : 's'} from “${removeTarget.label}” will be deleted from this spec.${
                removeTarget.modified ? ' The edits made to them here go with them.' : ''
              } Ctrl+Z brings them back; the module library is untouched.`
            : ''
        }
        confirmLabel={removeTarget?.modified ? 'Remove anyway' : 'Delete'}
        onConfirm={doRemove}
      />

      <ConfirmDialog
        open={!!acceptAllTarget}
        onOpenChange={(o) => !o && setAcceptAllTarget(null)}
        title="Replace this copy with the library version?"
        description={
          acceptAllTarget
            ? `“${acceptAllTarget.moduleName}” was edited in this spec. Taking the whole update replaces every paragraph of it with the library's — the edits made here are lost (Ctrl+Z brings them back). Answer paragraph by paragraph instead to keep some of them.`
            : ''
        }
        confirmLabel="Take the update"
        onConfirm={() => {
          const target = acceptAllTarget;
          setAcceptAllTarget(null);
          if (target) applyAll(target.entryId, 'accepted');
        }}
      />

      <ConfirmDialog
        open={!!leaveProceed}
        onOpenChange={(o) => !o && setLeaveProceed(null)}
        title="Leave with chunks unplaced?"
        description={`${chips.length} chunk${
          chips.length === 1 ? '' : 's'
        } from the module library are still waiting for a spot. They are not saved anywhere — leaving discards them.`}
        confirmLabel="Leave and discard"
        onConfirm={() => {
          const run = leaveProceed?.run;
          setChips([]);
          setLeaveProceed(null);
          run?.();
        }}
      />
    </>
  );

  return {
    modules,
    loading,
    error,
    reload: load,
    busyModuleId,
    insertModule,
    chips,
    armedKey,
    arm,
    discard,
    stampAt,
    selectInsertion,
    requestRemoveInsertion,
    inDocument,
    revealInsertion,
    requestRemoveByInsertId,
    localUpdates,
    ownsEntry,
    decideLocal,
    decideLocalAll,
    focusLocalChangeId,
    clearLocalFocus: React.useCallback(() => setFocusLocalChangeId(null), []),
    reviewInsertion,
    reviewFor,
    decideReview,
    closeReview,
    guardLeave,
    panelReveal,
    overlay,
  };
}
